import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import Background from "@/components/landing/Background";
import AppHeader from "@/components/nav/AppHeader";
import { CheckCircle2, AlertTriangle, Loader2, Mail } from "lucide-react";

/**
 * VerifyEmail page — handles the link from the verification email.
 *
 * URL: /verify-email?token=<raw_token>
 *
 * The backend (/auth/verify-email) is idempotent: on first click it marks the
 * user verified; on subsequent clicks it returns 400 because the token row
 * was deleted. We surface a friendly UA message either way.
 *
 * After success we refresh the global auth state so the unverified banner
 * disappears from Dashboard immediately when the user navigates back.
 */
const VerifyEmail = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { user, refresh } = useAuth();
    const hasProcessed = useRef(false);

    // Possible states: "verifying" → "success" | "already" | "invalid" | "error"
    const [status, setStatus] = useState("verifying");
    const [errorMsg, setErrorMsg] = useState("");

    useEffect(() => {
        // StrictMode double-fires effects — guard so we don't call /verify-email
        // twice (which would always show "already" on the second call).
        if (hasProcessed.current) return;
        hasProcessed.current = true;

        const params = new URLSearchParams(location.search);
        const token = params.get("token");

        if (!token) {
            setStatus("invalid");
            return;
        }

        (async () => {
            try {
                await api.post("/auth/verify-email", { token });
                setStatus("success");
                // Refresh /auth/me so the Dashboard banner disappears.
                try { await refresh(); } catch {}
            } catch (err) {
                const detail = err?.response?.data?.detail;
                const code = err?.response?.status;
                if (code === 400 || code === 410) {
                    // Token already used or expired — but the user may already
                    // be verified. Check via /auth/me.
                    try {
                        const me = await api.get("/auth/me");
                        if (me.data?.email_verified_at) {
                            setStatus("already");
                        } else {
                            setStatus("invalid");
                            setErrorMsg(typeof detail === "string" ? detail : "Посилання застаріло.");
                        }
                    } catch {
                        setStatus("invalid");
                        setErrorMsg(typeof detail === "string" ? detail : "Посилання застаріло.");
                    }
                } else {
                    setStatus("error");
                    setErrorMsg(typeof detail === "string" ? detail : "Сервер недоступний. Спробуйте ще раз.");
                }
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const goNext = () => {
        // If the user is logged in, send them to dashboard/onboarding;
        // otherwise to login so they can sign in.
        if (user) {
            if (!user.plan) navigate("/pricing", { replace: true });
            else if (user.onboarding_step !== "done") navigate("/onboarding", { replace: true });
            else navigate("/dashboard", { replace: true });
        } else {
            navigate("/login", { replace: true });
        }
    };

    return (
        <div className="min-h-screen relative" data-testid="verify-email-page">
            <Background />
            <div className="relative z-10 min-h-screen flex flex-col">
                <AppHeader mode="public" />
                <main className="flex-1 flex items-center justify-center px-6 py-8">
                    <div className="w-full max-w-[440px]">
                        <div className="glass rounded-2xl p-8 text-center">
                            {status === "verifying" && (
                                <div data-testid="verify-state-loading">
                                    <Loader2 className="mx-auto text-primary animate-spin" size={40} />
                                    <h1 className="mt-6 font-display font-extrabold text-2xl text-text-primary">
                                        Підтверджуємо email…
                                    </h1>
                                    <p className="mt-3 text-sm text-secondary-c">
                                        Це займе кілька секунд.
                                    </p>
                                </div>
                            )}

                            {status === "success" && (
                                <div data-testid="verify-state-success">
                                    <CheckCircle2 className="mx-auto text-success" size={48} />
                                    <h1 className="mt-6 font-display font-extrabold text-2xl text-text-primary">
                                        Email підтверджено!
                                    </h1>
                                    <p className="mt-3 text-sm text-secondary-c leading-relaxed">
                                        Ваш акаунт повністю активований. Тепер ви можете користуватись усіма функціями AutoAds.
                                    </p>
                                    <button
                                        onClick={goNext}
                                        data-testid="verify-success-continue"
                                        className="mt-6 w-full h-10 rounded-md bg-primary text-on-primary font-medium text-sm hover:bg-primary/90 transition"
                                    >
                                        Продовжити →
                                    </button>
                                </div>
                            )}

                            {status === "already" && (
                                <div data-testid="verify-state-already">
                                    <Mail className="mx-auto text-primary" size={48} />
                                    <h1 className="mt-6 font-display font-extrabold text-2xl text-text-primary">
                                        Email уже підтверджено
                                    </h1>
                                    <p className="mt-3 text-sm text-secondary-c leading-relaxed">
                                        Ваш email був підтверджений раніше — нічого робити не потрібно.
                                    </p>
                                    <button
                                        onClick={goNext}
                                        data-testid="verify-already-continue"
                                        className="mt-6 w-full h-10 rounded-md bg-primary text-on-primary font-medium text-sm hover:bg-primary/90 transition"
                                    >
                                        Продовжити →
                                    </button>
                                </div>
                            )}

                            {(status === "invalid" || status === "error") && (
                                <div data-testid={`verify-state-${status}`}>
                                    <AlertTriangle className="mx-auto text-warning" size={48} />
                                    <h1 className="mt-6 font-display font-extrabold text-2xl text-text-primary">
                                        {status === "invalid"
                                            ? "Посилання застаріло"
                                            : "Не вдалося підтвердити"}
                                    </h1>
                                    <p className="mt-3 text-sm text-secondary-c leading-relaxed">
                                        {status === "invalid"
                                            ? "Це посилання вже використане або термін його дії вичерпано (24 години). Запитайте новий лист підтвердження зі сторінки налаштувань."
                                            : (errorMsg || "Спробуйте відкрити посилання ще раз.")}
                                    </p>
                                    <button
                                        onClick={goNext}
                                        data-testid="verify-fail-back"
                                        className="mt-6 w-full h-10 rounded-md border border-border bg-surface hover:bg-surface-hover transition text-text-primary font-medium text-sm"
                                    >
                                        {user ? "До дашборду" : "До входу"}
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </main>
            </div>
        </div>
    );
};

export default VerifyEmail;
